/**
 * Complimentary Services (Business Controls, v108): gift any purchasable service to an existing booking at $0 without
 * changing the master price. Uses the existing admin add-on route in its `complimentary` mode, which already writes the
 * $0 add-on row, mirrors it to the client portal, grants bonus hours where the add-on carries them, notifies the roles
 * that act on it, and audits who / why / regular value. Nothing here charges a card.
 */
import { useEffect, useState } from 'react';
import { Loader2, Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Input } from '@/components/admin/ui/input';
import { Label } from '@/components/admin/ui/label';
import { Textarea } from '@/components/admin/ui/textarea';
import { Button } from '@/components/admin/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { GIFT_SESSION_TYPES } from '@/lib/booking/giftSession';

/** "New booking" in the Booking dropdown (Traci, 28 Sep 2026): gift a session before the client knows the date. */
const NEW_BOOKING = '__new__';

const dollars = (c: number) => '$' + (Math.round(Number(c || 0)) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const shortDate = (d?: string | null) => (d ? new Date(String(d).slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—');

async function adminApi(path: string, init: RequestInit = {}) {
  const { data: { session } } = await supabaseAdmin.auth.getSession();
  if (!session) throw new Error('Sign in again.');
  const res = await fetch(path, { ...init, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, ...(init.headers || {}) } });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || json.error || `Request failed (${res.status})`);
  return json;
}

export default function ComplimentaryServices() {
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState<any[]>([]);
  const [client, setClient] = useState<any | null>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [bookingId, setBookingId] = useState('');
  const [catalog, setCatalog] = useState<any[]>([]);
  const [addonId, setAddonId] = useState('');
  const [sessionType, setSessionType] = useState('engagement');
  const isNew = bookingId === NEW_BOOKING;
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState<any[] | null>(null);

  useEffect(() => { adminApi('/api/admin/addons/catalog').then((j) => setCatalog((j.addons || []).filter((a: any) => a.is_active !== false && !a.metadata?.promo_only && !a.metadata?.is_fee))).catch((e) => toast.error(e.message)); }, []);

  const loadRecent = async () => {
    const { data, error } = await supabaseAdmin
      .from('booking_addons')
      .select('id, purchased_at, notes, unit_price_cents, total_price_cents, addon:addons(name, price_cents), booking:bookings(id, event_date, customer:users!bookings_customer_id_fkey(full_name))')
      .eq('total_price_cents', 0).ilike('notes', '%complimentary%').order('purchased_at', { ascending: false }).limit(25);
    if (error) toast.error(error.message); else setRecent(data || []);
  };
  useEffect(() => { loadRecent(); }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setClients([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await (supabaseAdmin.from('users') as any).select('id, full_name, email').eq('role', 'customer').or(`full_name.ilike.%${query.trim()}%,email.ilike.%${query.trim()}%`).limit(8);
      if (!cancelled) setClients(data || []);
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const pickClient = async (c: any) => {
    setClient(c); setClients([]); setQuery(c.full_name || c.email); setBookingId('');
    // is_test is newer than the generated types; the cast keeps the type ratchet honest without widening anything else
    const { data } = await (supabaseAdmin.from('bookings') as any).select('id, event_date, booking_status, package_type, location_city, location_state').eq('customer_id', c.id).eq('is_test', false).order('event_date', { ascending: false });
    setBookings(data || []);
    if (data && data.length === 1) setBookingId(data[0].id);
  };

  const addon = catalog.find((a) => a.id === addonId) || null;
  const grant = async () => {
    if (reason.trim().length < 8) { toast.error('An internal reason of at least 8 characters is required'); return; }
    if (isNew) {
      if (!client) { toast.error('Pick a client'); return; }
      setSaving(true);
      try {
        await adminApi('/api/post-event-addons/admin/create-gift', { method: 'POST', body: JSON.stringify({ clientUserId: client.id, sessionType, internalNote: reason.trim() }) });
        toast.success(`${GIFT_SESSION_TYPES.find((s) => s.type === sessionType)?.label || 'Session'} gifted. ${client.full_name || 'The client'} gets the purple popup in their portal to pick a date (within 60 days, at least 31 days out).`);
        setReason(''); setBookingId('');
        await pickClient(client);
      } catch (e: any) { toast.error(`Could not gift: ${e.message}`); } finally { setSaving(false); }
      return;
    }
    if (!bookingId || !addonId) { toast.error('Pick a booking and a service'); return; }
    setSaving(true);
    try {
      const json = await adminApi(`/api/admin/addons/bookings/${bookingId}`, { method: 'POST', body: JSON.stringify({ addonId, quantity: 1, unitPriceCents: 0, billing: 'complimentary', notes: reason.trim() }) });
      toast.success(`${addon?.name || 'Service'} granted at $0${json.bonusHours ? ` · +${json.bonusHours} hour${json.bonusHours === 1 ? '' : 's'}` : ''}. Client portal updated, note and audit written.`);
      setReason(''); setAddonId('');
      await loadRecent();
    } catch (e: any) { toast.error(`Could not grant: ${e.message}`); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Gift className="h-4 w-4" /> Complimentary Services</CardTitle>
          <p className="text-xs text-muted-foreground">Gift any normally purchasable service to an existing booking without changing its master price. On confirmation the client portal updates, the service is added to the booking at $0, a booking note records the service, reason, date and user, and the audit log records the regular value, actor, date and reason.</p></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2 relative">
            <Label htmlFor="cs-client">Client</Label>
            <Input id="cs-client" value={query} onChange={(e) => { setQuery(e.target.value); setClient(null); setBookings([]); }} placeholder="Name or email" autoComplete="off" />
            {clients.length > 0 && !client && (
              <ul className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md text-sm max-h-56 overflow-auto">
                {clients.map((c) => <li key={c.id}><button type="button" className="w-full text-left px-3 py-2 hover:bg-accent" onClick={() => pickClient(c)}>{c.full_name || 'Unnamed'}<span className="block text-xs text-muted-foreground">{c.email}</span></button></li>)}
              </ul>
            )}
            {client && <p className="text-xs text-muted-foreground">Resolved: <b>{client.full_name}</b> · {bookings.length} booking{bookings.length === 1 ? '' : 's'}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cs-booking">Booking</Label>
            <select id="cs-booking" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={bookingId} onChange={(e) => setBookingId(e.target.value)} disabled={!client}>
              <option value="">{client ? 'Select a booking…' : 'Pick a client first'}</option>
              {client && <option value={NEW_BOOKING}>New booking (client picks the date)</option>}
              {bookings.filter((b) => String(b.event_date || '').slice(0, 10) !== '2099-01-01').map((b) => <option key={b.id} value={b.id}>{shortDate(b.event_date)} · {b.booking_status}{b.location_city ? ` · ${b.location_city}` : ''} · {b.id.slice(0, 8)}</option>)}
            </select>
          </div>
          {isNew ? (
            <div className="space-y-2">
              <Label htmlFor="cs-session">Session</Label>
              <select id="cs-session" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={sessionType} onChange={(e) => setSessionType(e.target.value)}>
                {GIFT_SESSION_TYPES.map((s) => <option key={s.type} value={s.type}>{s.label} (2 hours)</option>)}
              </select>
              <p className="text-xs text-muted-foreground">A $0 booking is created; the client gets the purple popup to pick a date within 60 days, at least 31 days out.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="cs-service">Complimentary Service</Label>
              <select id="cs-service" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={addonId} onChange={(e) => setAddonId(e.target.value)}>
                <option value="">Select a service…</option>
                {catalog.map((a) => <option key={a.id} value={a.id}>{a.name} — regular value {a.metadata?.hourlyRate ? `$${a.metadata.hourlyRate}/hr by coverage` : dollars(a.price_cents)}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-2 md:col-span-3">
            <Label htmlFor="cs-reason">Internal Reason *</Label>
            <Textarea id="cs-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this is being gifted. Goes on the booking note and the audit log." />
          </div>
          <div className="md:col-span-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">{addon ? `Regular value ${addon.metadata?.hourlyRate ? 'depends on coverage hours' : dollars(addon.price_cents)} · recorded at $0` : ''}</p>
            <Button onClick={grant} disabled={saving || !bookingId || (!isNew && !addonId) || reason.trim().length < 8}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift className="h-4 w-4 mr-2" />}Grant Complimentary Service</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Recent Complimentary Services</CardTitle><p className="text-xs text-muted-foreground">Most recent grants, from the add-on rows recorded at $0.</p></CardHeader>
        <CardContent>
          {!recent ? <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
            <Table>
              <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Booking</TableHead><TableHead>Service</TableHead><TableHead>Regular value</TableHead><TableHead>Granted by / date</TableHead></TableRow></TableHeader>
              <TableBody>
                {recent.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No complimentary services recorded yet.</TableCell></TableRow> : recent.map((r: any) => {
                  const by = /\[admin ([^\]]+)\]/.exec(r.notes || '')?.[1] || 'staff';
                  return (
                    <TableRow key={r.id}>
                      <TableCell>{r.booking?.customer?.full_name || '—'}</TableCell>
                      <TableCell className="text-xs tabular-nums">{r.booking?.id?.slice(0, 8)} · {shortDate(r.booking?.event_date)}</TableCell>
                      <TableCell>{r.addon?.name || '—'}</TableCell>
                      <TableCell className="tabular-nums">{dollars(r.addon?.price_cents || 0)}</TableCell>
                      <TableCell className="text-xs">{by}<span className="block text-muted-foreground">{shortDate(r.purchased_at)}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
